import { useCallback, useMemo, useRef, useState } from 'react';
import { useAtomValue, useSetAtom, useStore } from 'jotai';
import type { ViewStateChangeEvent } from 'react-map-gl';
import type { Polygon } from 'geojson';

import { useMapResize } from 'components/map';
import { QueryMap, type BBox, type SpatialQuery } from 'components/query-map';

import { AssetLayers } from './asset-layers';
import {
  createMapRefAtom,
  mapQueryCameraAtom,
  mapQueryMapConfigAtom,
} from './atoms';
import { useTractSource } from './hooks';
import {
  ExtentOutline,
  FitToResults,
  MapLoadingNote,
  ZoomInNote,
} from './map-overlays';
import { TractLayer } from './tract-layer';
import type {
  BoundingBox,
  MapFilterMode,
  MapQueryFit,
  MapQueryListedShapes,
  MapQueryNonTractFeatures,
  MapQueryTicks,
} from './types';
import { boundingBoxOf, narrowsResults } from './utils';
import styles from './map-query-source.module.scss';

// Texas, at the zoom the wizard's other map opens at.
const DEFAULT_CAMERA = { latitude: 31.25, longitude: -99.25, zoom: 5 };

/** The focused result (its `resultRowId`) and the box the map flies to. */
export interface MapQueryFocus {
  id: string;
  bbox: BBox;
}

export interface MapQueryMapProps {
  /** The result tiles hold what a ticked entity holds on this date too. */
  asOfDate: string | null;
  mode: MapFilterMode;
  polygon: Polygon | null;
  onPolygonChange: (polygon: Polygon | null) => void;
  aoiEditing: boolean;
  onAoiEditingChange: (editing: boolean) => void;
  /** The visible extent the search is using. */
  extent: BoundingBox | null;
  /**
   * The visible extent the search was sent, once the map held still: the tract
   * tiles ask for it, so they reload with the results, not on every move.
   */
  searchedExtent: BoundingBox | null;
  onExtentChange: (extent: BoundingBox) => void;
  ticks: MapQueryTicks;
  /** The listed Tracts' shapes; null with none listed. */
  listedShapes: MapQueryListedShapes | null;
  /** The matching wellbore and LTD assets' shapes; undefined before the search answers. */
  nonTractFeatures: MapQueryNonTractFeatures | undefined;
  focus: MapQueryFocus | null;
  /** A search of changed filters, or of the listed Tracts' shapes, is running. */
  searching: boolean;
  /** The results box to fit to once for its filters; null for none. */
  fit: MapQueryFit | null;
  /**
   * A click on a result drawn (a Tract, a listed Tract, a wellbore or LTD
   * asset), with its row id and the box of its drawn part. Kept stable by the
   * caller, so the click listeners (and the pointer) stay put.
   */
  onTractClick: (id: string, bbox: BBox) => void;
}

/**
 * The Map Query map: `QueryMap` reporting the visible extent (`bounds`) or
 * drawing the AOI (`polygon`) for the Map Filter mode, the tract layer and the
 * asset layers (listed Tracts, wellbore and LTD assets), the dashed outline of
 * the searched extent, and the "Zoom in" note. The tract and asset layers wait
 * for the tracts map source together, so the asset layers are added over the
 * tract layers. Focusing a result flies the map there without re-running the
 * extent search.
 *
 * It opens where it was last left in this wizard. Opened under Use Visible Map
 * Extent with an extent already searched (Back from Step 1), it keeps that extent
 * until the user moves the map: the reopened map measures a rectangle a little
 * off the stored one, and taking it would re-run the search and make an unchanged
 * Next rebuild the draft.
 *
 * @param {MapQueryMapProps} props - Map filter state, ticks and focus
 * @return {JSX.Element} The map
 */
export function MapQueryMap({
  asOfDate,
  mode,
  polygon,
  onPolygonChange,
  aoiEditing,
  onAoiEditingChange,
  extent,
  searchedExtent,
  onExtentChange,
  ticks,
  listedShapes,
  nonTractFeatures,
  focus,
  searching,
  fit,
  onTractClick,
}: MapQueryMapProps) {
  const refAtom = useMemo(() => createMapRefAtom(), []);
  const mapRef = useAtomValue(refAtom);
  const store = useStore();
  const [camera] = useState(
    () => store.get(mapQueryCameraAtom) ?? DEFAULT_CAMERA
  );
  const setCamera = useSetAtom(mapQueryCameraAtom);
  const tractSource = useTractSource();
  const keepStoredExtent = useRef(mode === 'extent' && extent !== null);

  // Only a move the user makes carries the event that started it; the map's own
  // moves on opening and resizing do not.
  const handleMoveStart = useCallback(
    ({ originalEvent }: ViewStateChangeEvent) => {
      if (originalEvent) keepStoredExtent.current = false;
    },
    []
  );
  const handleMoveEnd = useCallback(
    ({ viewState: { latitude, longitude, zoom } }: ViewStateChangeEvent) =>
      setCamera({ latitude, longitude, zoom }),
    [setCamera]
  );

  // mapbox-gl resizes with the window only; the panes beside it can resize it too.
  const paneRef = useRef<HTMLDivElement>(null);
  useMapResize(paneRef, mapRef);

  const handleViewport = useCallback(
    (viewport: Polygon | null) => {
      if (viewport && !keepStoredExtent.current) {
        onExtentChange(boundingBoxOf(viewport));
      }
    },
    [onExtentChange]
  );

  const query = useMemo<SpatialQuery | undefined>(() => {
    if (mode === 'extent') return { mode: 'bounds', onChange: handleViewport };
    if (mode === 'aoi') {
      return {
        mode: 'polygon',
        polygon,
        onChange: onPolygonChange,
        editing: aoiEditing,
        onEditingChange: onAoiEditingChange,
        // A click inside the polygon focuses the Tract under it; Edit edits the polygon.
        selectOnClick: false,
      };
    }
    return undefined;
  }, [
    mode,
    handleViewport,
    polygon,
    onPolygonChange,
    aoiEditing,
    onAoiEditingChange,
  ]);

  const fitToBounds = useMemo(() => (focus ? [focus.bbox] : []), [focus]);

  return (
    <div ref={paneRef} className={styles.mapPane}>
      <QueryMap
        className={styles.map}
        latitude={camera.latitude}
        longitude={camera.longitude}
        zoom={camera.zoom}
        onMoveStart={handleMoveStart}
        onMoveEnd={handleMoveEnd}
        mapConfigAtom={mapQueryMapConfigAtom}
        refAtom={refAtom}
        showControls
        fitToBounds={fitToBounds}
        quietFit
        query={query}
      >
        {tractSource && (
          <>
            <TractLayer
              source={tractSource}
              asOfDate={asOfDate}
              ticks={ticks}
              polygon={mode === 'aoi' ? polygon : null}
              focusedTractId={focus?.id ?? null}
              extent={mode === 'extent' ? searchedExtent : null}
              // Not while a polygon is being drawn or its corners edited.
              onResultClick={
                mode === 'aoi' && (polygon === null || aoiEditing)
                  ? undefined
                  : onTractClick
              }
            />
            <AssetLayers
              listed={listedShapes}
              nonTracts={nonTractFeatures}
              entityIds={ticks.entityIds}
              focusedId={focus?.id ?? null}
              tractMinZoom={tractSource.minzoom}
            />
          </>
        )}
        {mode === 'extent' && extent && <ExtentOutline extent={extent} />}
        <ZoomInNote
          narrowed={narrowsResults(ticks)}
          tractMinZoom={tractSource?.minzoom ?? null}
        />
        <MapLoadingNote searching={searching} />
        <FitToResults fit={fit} />
      </QueryMap>
    </div>
  );
}
